























import Enumerable from "linq";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Section } from "../../../../domain/document";
import {
  Point,
  Size,
  Rectangle,
} from "../../../../domain/drawing";
import {
  ModelEditableMixin,
  MouseTrackableMixin,
  ComponentWatchableMixin,
  PdfStyleDrawableMixin,
} from "../../../system/mixins";
import {
  ScrollIntoView,
} from "../../../system/utilities";
import {
  DocumentStructureEditableMixin,
  DocumentPageNavigatableMixin,
} from "../mixins";
import PageSilhouetteIndicator from "./PageSilhouetteIndicator.vue";

interface IPageDrawable {
  page: number;
  area: Rectangle;
  layer: Rectangle;
  isFocused: boolean;
  isCurrent: boolean;
}

@Component({
  components: {
    PageSilhouetteIndicator,
  },
})
export default class PageSilhouetteSelector extends Mixins<
  ModelEditableMixin 
  & MouseTrackableMixin 
  & ComponentWatchableMixin 
  & DocumentStructureEditableMixin 
  & DocumentPageNavigatableMixin 
  & PdfStyleDrawableMixin
>(
  ModelEditableMixin,
  MouseTrackableMixin,
  ComponentWatchableMixin,
  DocumentStructureEditableMixin,
  DocumentPageNavigatableMixin,
  PdfStyleDrawableMixin
) {
  @Prop({ default: true })
  isScrollable: boolean;

  data() {
    return {
      rootWidth:  null as number,
      scrollberWidth: null as number,
      size: new Size(0, 0),
      lastFocusedPage: 0,
    };
  }
  rootWidth?: number;
  scrollberWidth?: number;
  size: Size;
  lastFocusedPage: number;

  get rootStyle(): any {
    return {
      width: this.rootWidth ? this.rootWidth + "px" : null,
    };
  }

  getPageSize(page: number): Size {
    return (this.isScrollable || (page === this.focusedPage))
      ? this.focusedPageSize
      : this.unfocusedPageSize;
  }

  get pageSizeStyle(): any {
    return {
      width: "100%",
      height: this.size.width + "px",
    };
  }

  get drawers(): IPageDrawable[] {
    return Enumerable.from(this.sections)
      .select((s, i) => this.createDrawer(s, i + 1))
      .toArray();
  }

  private createDrawer(section: Section, page: number): IPageDrawable {
    const yOfArea = this.unfocusedPageSize.height * (page - 1)
      + ((this.focusedPage < page) ? (this.focusedPageSize.height - this.unfocusedPageSize.height) : 0);
    const isFocused = (page === this.focusedPage);
    const area = new Rectangle(new Point(0, yOfArea), isFocused ? this.focusedPageSize : this.unfocusedPageSize);
    const offsetSize = new Size(area.size.width - 2, area.size.height - 2);
    const scale = Math.min(offsetSize.width / section.size.width, offsetSize.height / section.size.height);
    const size = section.size.multiply(scale);
    return {
      page,
      area,
      layer: new Rectangle(new Point(1 + (offsetSize.width - size.width) / 2, area.y + 1 + (offsetSize.height - size.height) / 2), size),
      isFocused,
      isCurrent: (page === this.currentPage),
    };
  }

  get focusedPageSize(): Size {
    return new Size(30, 30);
  }

  get unfocusedPageSize(): Size {
    return new Size(
      30,
      ((this.sections.length * this.focusedPageSize.height) < this.size.height)
        ? this.focusedPageSize.height
        : (this.size.height - this.focusedPageSize.height) / (this.sections.length - 1));
  }

  get focusedPage(): number {
    return this.isMouseTracking
      ? this.lastMouseTrackingPage
      : this.currentPage;
  }

  @Watch("focusedPage")
  onFocusedPageChanged(newValue: number, oldValue: number): void {
    this.lastFocusedPage = newValue;
  }

  @Watch("currentPage")
  onCurrentPageChanged(newValue: number, oldValue: number): void {
    this.scrollIntoView();
  }

  get lastMouseTrackingPage(): number {
    return this.lastFocusedPageRectangle.contains(this.mouseLocation)
      ? this.lastFocusedPage
      : (this.mouseLocation.y < this.lastFocusedPageRectangle.topLeft.y)
        ? Math.ceil(this.mouseLocation.y / this.unfocusedPageSize.height)
        : this.lastFocusedPage + Math.ceil((this.mouseLocation.y - this.lastFocusedPageRectangle.bottomRight.y) / this.unfocusedPageSize.height);
  }

  get lastFocusedPageRectangle(): Rectangle {
    return new Rectangle(
      this.unfocusedPageSize.toPoint().multiply(this.lastFocusedPage - 1),
      this.focusedPageSize
    );
  }

  mounted(): void {
    this.size = this.watchHtmlElementResized("root", (n: Size, o: Size) => this.onResized(n, o));
    this.updateRootWidth();
    this.scrollIntoView();
  }

  onResized(n: Size, o: Size): void {
    this.size = n ? n : new Size(0, 0);
    this.updateRootWidth();
  }

  onClicked(e: PointerEvent): void {
    this.currentPage = this.lastMouseTrackingPage;
  }

  updateRootWidth() {
    const elem = this.$refs.root as HTMLElement;
    if (elem.scrollHeight !== elem.clientHeight) {
      if (!this.scrollberWidth) {
        this.scrollberWidth = elem.scrollWidth - elem.clientWidth;
      }
      this.rootWidth = elem.scrollWidth + this.scrollberWidth;
    } else {
      this.rootWidth = null;
    }
  }

  scrollIntoView(): void {
    ScrollIntoView.vertical(this.$refs.root as Element, this.currentPage - 1);
  }

  onPageSilhouetteClicked(pageNumber: number): void {
    if (!this.isSourceAlreadyInitialized) {
      this.$logger.info("初期化が終わっていないので、ページ選択できません。");
      return;
    }
    this.currentPage = pageNumber;
  }
}
